import { createContext, useContext, type ReactNode } from 'react'
import { useUser, useSignIn, useSignUp, useSignOut, useAutoSignIn, useSignInWithGoogle } from '@/lib/queries'
import type { User } from '@/lib/pocketbase'

interface AuthContextType {
  user: User | null
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, passwordConfirm: string, name?: string, username?: string, birthdate?: string, gender?: 'male' | 'female') => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => void
  isLoading: boolean
  isSigningIn: boolean
  isSigningUp: boolean
  isSigningOut: boolean
  isSigningInWithGoogle: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { data: user, isLoading } = useUser()
  const signInMutation = useSignIn()
  const signUpMutation = useSignUp()
  const autoSignInMutation = useAutoSignIn()
  const signInWithGoogleMutation = useSignInWithGoogle()
  const signOutMutation = useSignOut()

  const handleSignIn = async (email: string, password: string) => {
    try {
      await signInMutation.mutateAsync({ email, password })
    } catch (error) {
      console.error('Sign in error:', error)
      throw error
    }
  }

  const handleSignUp = async (email: string, password: string, passwordConfirm: string, name?: string, username?: string, birthdate?: string, gender?: 'male' | 'female') => {
    try {
      // First create the user
      await signUpMutation.mutateAsync({ email, password, passwordConfirm, name, username, birthdate, gender })
      // Then automatically sign them in
      await autoSignInMutation.mutateAsync({ email, password })
    } catch (error) {
      console.error('Sign up error:', error)
      throw error
    }
  }

  const handleSignInWithGoogle = async () => {
    try {
      await signInWithGoogleMutation.mutateAsync()
    } catch (error) {
      console.error('Google sign in error:', error)
      throw error
    }
  }

  const handleSignOut = () => {
    signOutMutation.mutate()
  }

  const value: AuthContextType = {
    user: user || null,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signInWithGoogle: handleSignInWithGoogle,
    signOut: handleSignOut,
    isLoading,
    isSigningIn: signInMutation.isPending,
    isSigningUp: signUpMutation.isPending || autoSignInMutation.isPending,
    isSigningOut: signOutMutation.isPending,
    isSigningInWithGoogle: signInWithGoogleMutation.isPending,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}